import React from "react";
import {
  AppstoreOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  FileSearchOutlined,
  FileZipOutlined,
  FolderOpenOutlined,
  RobotOutlined,
  SwapOutlined,
} from "@ant-design/icons";

/*
 * Shared category / sub-category presentation metadata.
 *
 * Extracted from "Table Columns.js" so the Task Library and the Results Dashboard render
 * the same pill from the SAME source. They previously would have needed two copies, and
 * two copies drift — the colour or an icon gets added in one place and not the other.
 *
 * These map STORED values → presentation only. No stored category or sub-category string
 * is changed anywhere; display text still goes through labelForCategory /
 * labelForSubCategory in reconciliationLabels.js.
 */

/**
 * Colours come from the ARC palette. Slate is deliberately RESERVED as the unmapped
 * fallback below and is not assigned to any category — otherwise a category someone
 * forgets to map would be indistinguishable from a real one.
 */
export const CATEGORY_META = {
  compare: { label: "Compare", color: "#185FA5" },          // accent blue
  "process automation": { label: "Process Automation", color: "#3B6D11" }, // pass green
  process: { label: "Process", color: "#6C8A9B" },

  // ─── Results Dashboard categories ───
  /*
   * Master Suite runs store the literal string "N/A" in BOTH task_category and
   * task_sub_category — it is the stored value, not missing data. Mapping it here is what
   * stops the Results table rendering a bare "N/A" that reads as a gap in the record.
   */
  "n/a": { label: "Master Suite", color: "#0B1F3A" },       // --arc-primary navy
  "ui automation": { label: "UI Automation", color: "#475569" }, // --arc-label
};

/**
 * Unmapped categories fall back to slate. Kept distinct from every assigned colour on
 * purpose, so an unmapped value is visibly "unknown" rather than impersonating a category.
 */
export const getCategoryMeta = (category) =>
  CATEGORY_META[(category || "").toLowerCase()] || {
    label: category || "—",
    color: "#6C8A9B",
  };

export const SUBCATEGORY_ICON = {
  "Event Monitor": <ClockCircleOutlined />,
  "File to File": <SwapOutlined />,
  "File to Specification": <FileSearchOutlined />,
  "File to Database": <DatabaseOutlined />,
  "Database to Database": <DatabaseOutlined />,
  file2Db: <DatabaseOutlined />,
  "Extract Compressed File": <FileZipOutlined />,
  extractCompressed: <FileZipOutlined />,
  "File Move, Copy or Delete": <FolderOpenOutlined />,
  "UI Test": <RobotOutlined />,
};

/*
 * Sub-category lookup is case-insensitive because the stored data is not consistent:
 * app_result_summary holds "File to Database" and "File To Database", and four separate
 * spellings of the File-to-Spec sub-category. Matching exactly would silently drop the
 * icon for whichever spelling was not listed.
 */
const SUBCATEGORY_ICON_LOWER = Object.fromEntries(
  Object.entries(SUBCATEGORY_ICON).map(([k, v]) => [k.toLowerCase(), v]),
);

export const getSubCategoryIcon = (subCategory) =>
  SUBCATEGORY_ICON_LOWER[(subCategory || "").toLowerCase()] || <AppstoreOutlined />;
